import { Button, LoadingIndicator } from "@light-technology/components";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { ArrowLeft } from "@untitledui/icons";
import { useState } from "react";
import { useParams } from "react-router";
import { api } from "../../state/api";
import {
  type AppConfigurationResponse,
  type AppResponse,
  type PlanDetailResponse,
} from "../../state/api.types";
import {
  useBatteryProgram,
  useEvChargingProgram,
} from "../../utilities/programHelper";
import { useDocumentTitle } from "../../utilities/useDocumentTitle";
import { useFormatPlanName } from "../../utilities/useFormatPlanName";
import { usePlanComparison } from "../PlanComparisonTable/common";
import { SkeletonLine } from "../shared/SkeletonLine";
import {
  toTermMonths,
  useEditPlanForm,
  type ServiceTerritory,
  type TermMonths,
} from "./common";
import { EditBatteryProgram } from "./EditBatteryProgram";
import { EditDesign } from "./EditDesign";
import { EditNameDescription } from "./EditNameDescription";
import { EditProgram } from "./EditProgram";
import { EditRenewalPlan } from "./EditRenewalPlan";
import { EditSolarBuyback } from "./EditSolarBuyback";
import { Footer } from "./Footer";
import { PlanEditComparisonTable } from "./PlanEditComparisonTable";
import { buildRenewalPlanOptions } from "./renewalPlan";

type PlanEditPageProps = {
  app: AppResponse;
};

export const PlanEditPage = ({ app }: PlanEditPageProps) => {
  const { planId } = useParams();
  const getPlan = api.useGetPlanQuery(planId ?? skipToken);
  const getConfiguration = api.useGetConfigurationQuery(app.uuid);
  const formatPlanName = useFormatPlanName();

  useDocumentTitle(getPlan.data?.name, "Plans");

  const isLoading = getPlan.isLoading || getConfiguration.isLoading;
  const isLoadingData = isLoading || !getPlan.data || !getConfiguration.data;

  return (
    <div className="flex h-full flex-col justify-between">
      <div className="flex flex-1 flex-col gap-4xl">
        <div className="flex flex-col gap-xl p-4xl">
          <Button
            iconLeading={ArrowLeft}
            size="sm"
            color="link-gray"
            href={`/apps/${app.slug}/plans/`}
          >
            Return to plans
          </Button>
          <div className="text-display-sm font-semibold">
            {getPlan.data && getConfiguration.data ? (
              `Edit the ${formatPlanName(getPlan.data.name)}`
            ) : (
              <SkeletonLine className="w-80" />
            )}
          </div>
        </div>
        {isLoadingData ? (
          <div className="mx-auto flex h-full w-full max-w-4xl flex-1 items-center justify-center">
            <LoadingIndicator type="brand-spinner" />
          </div>
        ) : (
          <PlanEditPageLoaded
            app={app}
            plan={getPlan.data!}
            configuration={getConfiguration.data!}
          />
        )}
      </div>
    </div>
  );
};

type PlanEditPageLoadedProps = {
  app: AppResponse;
  plan: PlanDetailResponse;
  configuration: AppConfigurationResponse;
};

const PlanEditPageLoaded = ({
  app,
  plan,
  configuration,
}: PlanEditPageLoadedProps) => {
  const { control, handleSubmit } = useEditPlanForm(plan);
  const [serviceTerritory, setServiceTerritory] =
    useState<ServiceTerritory>("ONCOR");
  const [termMonths, setTermMonths] = useState<TermMonths>(
    toTermMonths(plan.term_months.toString()),
  );
  const planComparison = usePlanComparison(plan, serviceTerritory, termMonths);
  const [isExpanded, setIsExpanded] = useState(false);
  const isPlanEditingDisabled = plan.is_economic_editing_disabled;
  const getAppPlans = api.useGetAppPlansQuery(app.uuid);
  const renewalPlanOptions = buildRenewalPlanOptions(
    plan,
    (getAppPlans.data?.data ?? []).map((p) => ({
      uuid: p.uuid,
      name: p.name,
      state: p.state,
      renewalPlanUuid: p.renewal_plan_uuid,
    })),
  );
  const battery_program_details = useBatteryProgram(plan)?.battery_details;
  const ev_charging_program = useEvChargingProgram(plan);

  return (
    <div className="flex h-full flex-col gap-xl">
      <div
        className={
          isExpanded
            ? "fixed inset-0 z-1 overflow-y-auto bg-primary"
            : "mx-auto flex w-full max-w-4xl flex-1 flex-col gap-6xl px-4xl pb-4xl @page-md:flex-row"
        }
      >
        {/*
         * In expanded mode this div is the single scrollable container so that
         * both columns share one scroll context and the sticky right rail has the
         * full content height to travel through. In collapsed mode `contents`
         * makes it transparent to layout so the outer div stays the flex parent.
         */}
        <div
          className={isExpanded ? "flex flex-row gap-6xl p-[2rem]" : "contents"}
        >
          <div className="flex flex-2 flex-col [&>*:not(:first-child)]:border-t [&>*:not(:first-child)]:border-secondary [&>*:not(:first-child)]:pt-5xl [&>*:not(:last-child)]:pb-5xl">
            <EditNameDescription control={control} />
            <EditDesign
              isEditingDisabled={isPlanEditingDisabled}
              plan={plan}
              configuration={configuration}
              control={control}
              setTermMonths={setTermMonths}
            />
            {plan.has_solar_buyback && (
              <EditSolarBuyback
                isEditingDisabled={isPlanEditingDisabled}
                plan={plan}
                control={control}
              />
            )}
            {battery_program_details && (
              <EditBatteryProgram program={battery_program_details} />
            )}
            {ev_charging_program && (
              <EditProgram program={ev_charging_program} />
            )}
            <EditRenewalPlan
              plan={plan}
              renewalPlanOptions={renewalPlanOptions}
              control={control}
            />
          </div>
          <PlanEditComparisonTable
            plan={plan}
            control={control}
            serviceTerritory={serviceTerritory}
            setServiceTerritory={setServiceTerritory}
            termMonths={termMonths}
            setTermMonths={setTermMonths}
            planComparison={planComparison}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
          />
        </div>
      </div>
      <Footer
        app={app}
        plan={plan}
        handleSubmit={handleSubmit}
        control={control}
        renewalPlanOptions={renewalPlanOptions}
      />
    </div>
  );
};
